if (!customElements.get('products-toolbar')) {
  class ProductsToolbar extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      this.filtersComponent = document.querySelector('facet-filters');
      this.filtersColumn = document.querySelector('.main-products-grid__filters');
      // SH Update: Changed to querySelectorAll to handle multiple layout switchers (mobile and desktop)
      this.layoutSwitchers = this.querySelectorAll('.products-toolbar__layout');
      this.compareToggle = document.getElementById('compare-switch');
      this.sortBy = this.querySelector('.products-toolbar__sort');
      this.productsGrid = document.querySelector('.main-products-grid');
      
      // SH Update: Add responsive layout handling
      this.handleResponsiveLayout();
      window.addEventListener('resize', this.debounce(this.handleResponsiveLayout.bind(this), 250));

      if (this.filtersColumn) {
        this.filterToggle = this.querySelector('.js-toggle-filters');
        this.filterToggle.addEventListener('click', this.toggleFilters.bind(this));
      }

      if (this.layoutSwitchers.length > 0) {
        // SH Update: Add event listeners to all layout switchers (mobile and desktop)
        this.layoutSwitchers.forEach(switcher => {
          switcher.addEventListener('change', this.handleLayoutChange.bind(this));
        });

        // SH Update: Restore layout from localStorage and sync radio buttons
        this.restoreLayoutFromStorage();
      }

      if (this.compareToggle) {
        this.compareToggle.addEventListener('change', this.toggleCompare.bind(this));

        const isChecked = theme.storageUtil.get('compare-toggle', true);
        if (isChecked !== null) {
          this.toggleCompare(null, isChecked, true);
        } else {
          this.toggleCompare(null, this.compareToggle.checked, false);
        }

        setTimeout(() => {
          this.classList.add('transition-ready');
          this.productsGrid.classList.add('transition-ready');
        }, 500);
      } else if (theme.settings.compareToggle === 'none') {
        this.toggleCompare(null, true, false);
      }

      if (!this.filtersComponent && this.sortBy) {
        this.sortBy.addEventListener('change', ProductsToolbar.handleSortByChange);
      }
    }

    /**
     * Updates the UI to reflect the compare state
     * @param {object} evt - Change event object
     * @param {boolean} checked - Whether compare is enabled or not (optional)
     * @param {boolean} persist - Whether to persist the new state (optional)
     */
    toggleCompare(evt, checked = null, persist = true) {
      const isChecked = evt ? evt.target.checked : checked;
      this.productsGrid.dataset.compare = isChecked;

      const compareButton = document.getElementById('compare-drawer-open');
      compareButton.classList.toggle('is-out', !isChecked);

      if (!evt && this.compareToggle) this.compareToggle.checked = isChecked;

      if (persist && !Shopify.designMode) {
        theme.storageUtil.set('compare-toggle', isChecked);
      }

      // Load scripts/styles if not already loaded
      if (isChecked) {
        const resourcesTag = document.getElementById('compare-resources');
        if (resourcesTag) {
          const resources = JSON.parse(resourcesTag.textContent);
          resources.forEach((resource) => {
            if (resource.type === 'js') {
              const script = document.createElement('script');
              script.src = resource.path;
              document.head.appendChild(script);
            } else if (resource.type === 'css') {
              const link = document.createElement('link');
              link.href = resource.path;
              link.type = 'text/css';
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }
          });
          resourcesTag.remove();
        }
      }
    }

    /**
     * Toggles open/closed state of the filters on desktop.
     */
    toggleFilters() {
      this.filterToggle.classList.toggle('is-active');
      this.filtersOpen = this.filterToggle.classList.contains('is-active');
      this.filterToggle.setAttribute('aria-expanded', this.filtersOpen);

      if (theme.mediaMatches.lg) {
        this.filtersColumn.classList.toggle('lg:js-hidden', !this.filtersOpen);
        const productsList = document.querySelector('.main-products-grid__results > .grid');

        productsList.className = this.filtersOpen
          ? productsList.dataset.filtersOpenClasses
          : productsList.dataset.filtersClosedClasses;
      } else {
        this.filtersComponent.open();
      }
    }

    /**
     * Handles 'change' events on the layout switcher buttons.
     * @param {object} evt - Event object.
     * @param {string} layout - 'grid-desktop', 'list-desktop', 'grid-mobile', or 'list-mobile' - the layout to change to.
     */
    handleLayoutChange(evt, layout) {
      const layoutValue = layout || evt.target.value;
      const isMobile = layoutValue.includes('mobile');
      const isDesktop = layoutValue.includes('desktop');
      
      // Update the appropriate data attribute based on device type
      if (isMobile) {
        this.productsGrid.dataset.mobileLayout = layoutValue;
        if (!layout) theme.storageUtil.set('products-mobile-layout', layoutValue);
      } else if (isDesktop) {
        this.productsGrid.dataset.desktopLayout = layoutValue;
        if (!layout) theme.storageUtil.set('products-desktop-layout', layoutValue);
      }
    }

    /**
     * Restores layout from localStorage and syncs radio buttons
     */
    restoreLayoutFromStorage() {
      if (!this.productsGrid) return;

      // Restore desktop layout from localStorage
      const desktopLayout = theme.storageUtil.get('products-desktop-layout');
      if (desktopLayout) {
        this.productsGrid.dataset.desktopLayout = desktopLayout;
        
        // Extract the layout type (list or grid) from the full value
        const layoutType = desktopLayout.split('-')[0]; // "list" from "list-desktop"
        const desktopToggleId = `${layoutType}-layout-opt-desktop`;
        const desktopToggle = document.getElementById(desktopToggleId);
        
        if (desktopToggle) {
          desktopToggle.checked = true;
        }
      }

      // Restore mobile layout from localStorage
      const mobileLayout = theme.storageUtil.get('products-mobile-layout');
      if (mobileLayout) {
        this.productsGrid.dataset.mobileLayout = mobileLayout;
        
        // Extract the layout type (list or grid) from the full value
        const layoutType = mobileLayout.split('-')[0]; // "list" from "list-mobile"
        const mobileToggleId = `${layoutType}-layout-opt-mobile`;
        const mobileToggle = document.getElementById(mobileToggleId);
        
        if (mobileToggle) {
          mobileToggle.checked = true;
        }
      }
    }

    /**
     * Handles responsive layout changes based on screen size
     * Note: This method is kept for future use but doesn't modify data-layout
     */
    handleResponsiveLayout() {
      if (!this.productsGrid) return;
      
      // This method can be used for other responsive behaviors
      // but no longer modifies the data-layout attribute
      const isMobile = window.innerWidth < 768;
      // You can add other responsive logic here if needed
    }

    /**
     * Debounce utility function
     * @param {Function} func - Function to debounce
     * @param {number} wait - Wait time in milliseconds
     * @returns {Function} Debounced function
     */
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    /**
     * Handles when a sort by dropdown is changed (and filtering is disabled)
     * @param {object} evt - Event object.
     */
    static handleSortByChange(evt) {
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.set('sort_by', evt.detail.selectedValue);
      urlObj.hash = 'products-toolbar';
      window.location.href = urlObj.toString();
    }
  }

  customElements.define('products-toolbar', ProductsToolbar);
}
